import { Editor } from './editor.mjs';
import { Library } from './library.mjs';
import { Scope } from './scope.mjs';
import { Tools } from './tools.mjs';
import { UI } from './ui.mjs';
import { decodeAudioFile, loadExoticProject, saveTB3Project } from './exotic-projects.mjs';
import { getCodeFromUrl, getUrlFromCode } from './url.mjs';

const editor = new Editor();
const library = new Library();
const scope = new Scope();
const tools = new Tools(() => editor.value, code => {
	editor.setValue(code);
	ui.setCodeSize(code);
	globalThis.bytebeat.updateUrl();
	globalThis.bytebeat.resetTime();
});
const ui = new UI();

globalThis.bytebeat = new class {
	constructor() {
		this.audioCtx = null;
		this.audioGain = null;
		this.audioRecordChunks = [];
		this.audioRecorder = null;
		this.audioWorkletNode = null;
		this.audioFiles = new Map();
		this.byteSample = 0;
		this.defaultSettings = {
			codeStyle: 'Atom Dark',
			colorDiagram: '#0080ff',
			colorStereo: 1,
			colorTimeCursor: '#80bbff',
			colorWaveform: '#ffffff',
			drawMode: scope.drawMode,
			drawScale: scope.drawScale,
			fftSize: scope.fftSize,
			isSeconds: false,
			autoUpdateLibrary: true,
			showAllSongs: library.showAllSongs,
			srDivisor: 1,
			customRangeBits: 10,
			quantizedLevels: 16,
			themeStyle: 'Rainbow Dark',
			volume: .5
		};
		this.isCompilationError = false;
		this.isNeedClear = false;
		this.isLagging = false;
		this.isPlaying = false;
		this.isRecording = false;
		this.lastUpdateTime = 0;
		this.mode = 'Bytebeat';
		this.playbackSpeed = 1;
		this.sampleRate = 8000;
		this.settings = this.defaultSettings;
		this.updateCounter = 0;
		this.init();
	}
	handleEvent(event) {
		let elem = event.target;
		const { classList } = elem;
		switch(event.type) {
		case 'change':
			if(elem.classList.contains('library-show-all')) {
				library.toggleAll(elem, elem.checked);
				this.saveSettings();
				return;
			}
			switch(elem.id) {
			case 'control-code-style': this.setCodeStyle(elem.value); break;
			case 'control-color-diagram': this.setColorDiagram(elem.value); break;
			case 'control-color-stereo':
				this.setColorStereo(+elem.value);
				ui.controlColorDiagramInfo.innerHTML = scope.getColorTest('colorDiagram');
				ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform');
				break;
			case 'control-color-timecursor': this.setColorTimeCursor(elem.value); break;
			case 'control-color-waveform': this.setColorWaveform(elem.value); break;
			case 'control-drawmode': this.setDrawMode(elem.value); break;
			case 'control-mode': this.setPlaybackMode(elem.value); break;
			case 'control-custom-range': this.setCustomRangeBits(elem.value); break;
			case 'control-quantized-beat': this.setQuantizedLevels(elem.value); break;
			case 'control-samplerate':
			case 'control-samplerate-select': this.setSampleRate(+elem.value); break;
			case 'control-theme-style': this.setThemeStyle(elem.value); break;
			}
			return;
		case 'click':
			switch(elem.tagName) {
			case 'svg': elem = elem.parentNode; break;
			case 'use': elem = elem.parentNode.parentNode; break;
			default:
				if(classList.contains('control-fast-multiplier')) {
					elem = elem.parentNode;
				}
			}
			switch(elem.id) {
			case 'canvas-container':
			case 'canvas-main':
			case 'canvas-play':
			case 'canvas-timecursor': this.playbackToggle(!this.isPlaying); break;
			case 'control-counter':
			case 'control-pause': this.playbackToggle(false); break;
			case 'control-expand': ui.expandEditor(); break;
			case 'control-link': ui.copyLink(); break;
			case 'control-play-backward': this.playbackToggle(true, true, -1); break;
			case 'control-play-forward': this.playbackToggle(true, true, 1); break;
			case 'control-rec': this.toggleRecording(); break;
			case 'control-reset': this.resetTime(); break;
			case 'control-scale': this.resetScopeAdjustment(); break;
			case 'control-scaledown': this.setScopeAdjustment(-1, elem); break;
			case 'control-scaleup': this.setScopeAdjustment(1); break;
			case 'control-srdivisor-down': this.setSRDivisor(-1); break;
			case 'control-srdivisor-up': this.setSRDivisor(1); break;
			case 'control-stop': this.playbackStop(); break;
			case 'control-counter-units': this.toggleCounterUnits(); break;
			default:
				switch(true) {
				case classList.contains('code-text'):
					this.loadCode(Object.assign({ code: elem.innerText },
						elem.hasAttribute('data-songdata') ? JSON.parse(elem.dataset.songdata) : {}));
					break;
				case classList.contains('code-load'): library.onclickCodeLoadButton(elem); break;
				case classList.contains('code-remix-load'): library.onclickRemixLoadButton(elem); break;
				case classList.contains('tool-action'): tools.run(elem.dataset.tool); break;
				case classList.contains('library-header'): library.onclickLibraryHeader(elem); break;
				case elem.id === 'library-load-all': library.loadAllLibraries(); break;
				case elem.id === 'library-clear-search': library.clearSearch(); break;
				case classList.contains('library-shelf-button'): library.jumpToShelf(elem); break;
				case elem.parentNode.classList.contains('library-header'):
					library.onclickLibraryHeader(elem.parentNode);
					break;
				case classList.contains('song-hash'):
					navigator.clipboard.writeText(elem.dataset.hash);
					event.preventDefault();
					break;
				}
			}
			return;
		case 'input':
			switch(elem.id) {
			case 'control-counter': this.oninputCounter(event); break;
			case 'control-volume': this.setVolume(false); break;
			case 'library-search': library.filter(elem.value); break;
			}
			return;
		case 'keydown':
			if(elem.id === 'control-counter') {
				this.oninputCounter(event);
			}
			if(event.key === '/' && elem.tagName !== 'INPUT' && elem.tagName !== 'TEXTAREA' &&
				!elem.isContentEditable) {
				event.preventDefault();
				library.focusSearch();
			}
			return;
		case 'mouseover':
			switch(true) {
			case classList.contains('code-load'):
				elem.title = `Click to play the ${ elem.dataset.type } code`;
				break;
			case classList.contains('code-text'): elem.title = 'Click to play this code'; break;
			case classList.contains('songs-header'): elem.title = 'Click to show/hide the songs'; break;
			case classList.contains('song-hash'):
				elem.title = 'Click to copy the song hash into clipboard';
				break;
			case classList.contains('tag-c'): elem.title = 'C-compatible code'; break;
			case classList.contains('tag-console'):
				elem.title = 'Outputs messages in the error console';
				break;
			case classList.contains('tag-drawing'):
				elem.title = 'Generates art in the visualiser\'s scope';
				break;
			case classList.contains('tag-sample'):
				elem.title = 'Uses encoded audio samples (PCM, for example)';
				break;
			case classList.contains('tag-slow'):
				elem.title = 'May be performance issues. Try switching Chrome/Firefox.';
				break;
			}
			return;
		}
	}
	async init() {
		try {
			this.settings = JSON.parse(localStorage.settings);
			if(this.settings.autoUpdateLibrary === undefined) {
				this.settings.autoUpdateLibrary = true;
				this.saveSettings();
			}
			if(this.settings.customRangeBits === undefined) this.settings.customRangeBits = 10;
			if(this.settings.quantizedLevels === undefined) this.settings.quantizedLevels = 16;
			scope.drawMode = this.settings.drawMode;
			scope.drawScale = this.settings.drawScale;
			scope.setFFTSize(+this.settings.fftSize || 10);
			library.showAllSongs = this.settings.showAllSongs;
			library.setAutoUpdate(this.settings.autoUpdateLibrary);
		} catch(err) {
			this.saveSettings();
		}
		this.setThemeStyle();
		await this.initAudio();
		if(document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.initAfterDom());
			return;
		}
		this.initAfterDom();
	}
	setAutoUpdateLibrary(enabled) {
		library.setAutoUpdate(enabled);
	}
	initAfterDom() {
		editor.init();
		ui.initElements();
		scope.initElements();
		library.initElements();
		tools.initElements();
		this.setVolume(true);
		this.setCounterUnits();
		this.setCodeStyle();
		this.setColorStereo();
		this.setColorDiagram();
		this.setColorWaveform();
		this.setColorTimeCursor();
		this.setScopeAdjustment(0);
		ui.setModeParameters(this.mode, this.settings.customRangeBits, this.settings.quantizedLevels);
		this.parseUrl();
		this.sendData({ drawMode: scope.drawMode });
		ui.controlDrawMode.value = scope.drawMode;
		ui.controlThemeStyle.value = this.settings.themeStyle;
		ui.controlCodeStyle.value = this.settings.codeStyle;
		ui.mainElem.addEventListener('click', this);
		ui.mainElem.addEventListener('change', this);
		ui.mainElem.addEventListener('input', this);
		ui.containerFixed.addEventListener('input', this);
		ui.containerFixed.addEventListener('keydown', this);
		ui.containerScroll.addEventListener('mouseover', this);
		this.initExoticProjects();
	}
	async initAudio() {
		this.audioCtx = new AudioContext({ latencyHint: 'balanced', sampleRate: 48000 });
		this.audioGain = new GainNode(this.audioCtx);
		this.audioGain.connect(this.audioCtx.destination);
		// Analyser for FFT mode
		scope.analyser = [this.audioCtx.createAnalyser(), this.audioCtx.createAnalyser()];
		scope.analyser[0].minDecibels = scope.analyser[1].minDecibels = scope.minDecibels;
		scope.analyser[0].maxDecibels = scope.analyser[1].maxDecibels = scope.maxDecibels;
		scope.setFFTAnalyzer();
		const splitter = this.audioCtx.createChannelSplitter(2);
		splitter.connect(scope.analyser[0], 0);
		splitter.connect(scope.analyser[1], 1);
		const analyserGain = new GainNode(this.audioCtx);
		analyserGain.connect(splitter);
		// AudioWorklet for main calculations processing
		await this.audioCtx.audioWorklet.addModule('./build/audio-processor.mjs?version=2026092004');
		this.audioWorkletNode = new AudioWorkletNode(this.audioCtx, 'audioProcessor',
			{ outputChannelCount: [2] });
		this.audioWorkletNode.port.addEventListener('message', event => this.receiveData(event.data));
		this.audioWorkletNode.port.start();
		this.audioWorkletNode.connect(this.audioGain);
		this.audioWorkletNode.connect(analyserGain);
		// Recorder for recording audio files
		const mediaDest = this.audioCtx.createMediaStreamDestination();
		const audioRecorder = this.audioRecorder = new MediaRecorder(mediaDest.stream);
		audioRecorder.addEventListener('dataavailable', event => this.audioRecordChunks.push(event.data));
		audioRecorder.addEventListener('stop', () => {
			let fileName, type;
			const types = ['audio/webm', 'audio/ogg'];
			const files = ['track.webm', 'track.ogg'];
			while((fileName = files.pop()) && !MediaRecorder.isTypeSupported(type = types.pop())) {
				if(types.length === 0) {
					console.error('Recording is not supported in this browser!');
					break;
				}
			}
			const url = URL.createObjectURL(new Blob(this.audioRecordChunks, { type }));
			ui.downloader.href = url;
			ui.downloader.download = fileName;
			ui.downloader.click();
			setTimeout(() => window.URL.revokeObjectURL(url));
		});
		this.audioGain.connect(mediaDest);
	}
	loadCode({ code, sampleRate, mode, drawMode, scale }, isPlay = true) {
		this.mode = ui.controlPlaybackMode.value = mode = mode || 'Bytebeat';
		ui.setModeParameters(mode, this.settings.customRangeBits, this.settings.quantizedLevels);
		editor.setValue(code);
		this.setSampleRate(ui.controlSampleRate.value = +sampleRate || 8000, false);
		this.setSRDivisor(0);
		const data = {
			mode,
			customRangeBits: this.settings.customRangeBits,
			quantizedLevels: this.settings.quantizedLevels,
			sampleRate: this.sampleRate,
			sampleRatio: this.sampleRate / this.audioCtx.sampleRate
		};
		if(isPlay) {
			data.playbackSpeed = this.playbackSpeed = 1;
			this.playbackToggle(true, false);
			data.resetTime = true;
			data.isPlaying = isPlay;
		}
		data.setFunction = code;
		if(drawMode) {
			ui.controlDrawMode.value = scope.drawMode = drawMode;
			scope.toggleTimeCursor();
			scope.clearCanvas();
			this.saveSettings();
		}
		if(scale !== undefined) {
			this.setScale(scale - scope.drawScale);
		}
		this.sendData(data);
	}
	initExoticProjects() {
		const audioInput = document.getElementById('exotic-audio-input');
		const addAudioButton = document.getElementById('exotic-audio-add');
		const clearAudioButton = document.getElementById('exotic-audio-clear');
		const projectInput = document.getElementById('exotic-project-input');
		const loadProjectButton = document.getElementById('exotic-project-load');
		const saveProjectButton = document.getElementById('exotic-project-save');
		if(!audioInput || !addAudioButton || !clearAudioButton || !projectInput ||
			!loadProjectButton || !saveProjectButton) {
			return;
		}
		addAudioButton.addEventListener('click', () => audioInput.click());
		clearAudioButton.addEventListener('click', () => this.clearExoticAudio());
		audioInput.addEventListener('change', event => this.addExoticAudio(event.target.files));
		loadProjectButton.addEventListener('click', () => projectInput.click());
		projectInput.addEventListener('change', event => this.loadExoticProjectFile(event.target.files[0]));
		saveProjectButton.addEventListener('click', () => this.saveExoticProject());
		this.updateExoticAudioList();
	}
	async addExoticAudio(fileList) {
		if(!fileList || !fileList.length) {
			return;
		}
		const status = document.getElementById('exotic-project-status');
		for(const file of fileList) {
			if(!file.type.startsWith('audio/')) {
				continue;
			}
			try {
				const audioData = await decodeAudioFile(this.audioCtx, file);
				let index = 0;
				while(this.audioFiles.has(index)) {
					++index;
				}
				this.audioFiles.set(index, audioData);
			} catch(error) {
				if(status) {
					status.textContent = 'Unable to load ' + file.name + ': ' + error.message;
				}
			}
		}
		this.updateExoticAudioList();
		this.sendExoticAudioToProcessor();
	}
	updateExoticAudioList() {
		const list = document.getElementById('exotic-audio-list');
		if(!list) {
			return;
		}
		list.replaceChildren();
		if(!this.audioFiles.size) {
			list.textContent = 'No audio files loaded.';
			return;
		}
		for(const [index, audioData] of this.audioFiles) {
			const row = document.createElement('div');
			row.className = 'exotic-audio-row';
			const label = document.createElement('span');
			label.textContent = index + ': ' + audioData.name + ' (' + audioData.channels + 'ch, ' +
				audioData.sampleRate + 'Hz)';
			const remove = document.createElement('button');
			remove.className = 'code-button';
			remove.type = 'button';
			remove.textContent = 'Remove';
			remove.addEventListener('click', () => {
				this.audioFiles.delete(index);
				this.updateExoticAudioList();
				this.sendExoticAudioToProcessor();
			});
			row.append(label, remove);
			list.append(row);
		}
	}
	clearExoticAudio() {
		this.audioFiles.clear();
		this.updateExoticAudioList();
		this.sendExoticAudioToProcessor();
	}
	sendExoticAudioToProcessor() {
		this.sendData({ audioFiles: Array.from(this.audioFiles.entries()) });
	}
	async loadExoticProjectFile(file) {
		if(!file) {
			return;
		}
		const status = document.getElementById('exotic-project-status');
		if(status) {
			status.textContent = 'Loading ' + file.name + '…';
		}
		try {
			const project = await loadExoticProject(file);
			this.audioFiles = project.audioFiles;
			this.updateExoticAudioList();
			this.sendExoticAudioToProcessor();
			this.loadCode({ code: project.code, ...project.settings }, true);
			if(status) {
				status.textContent = project.format + ' project loaded.';
			}
		} catch(error) {
			if(status) {
				status.textContent = 'Unable to load project: ' + error.message;
			}
		}
	}
	async saveExoticProject() {
		const status = document.getElementById('exotic-project-status');
		try {
			const blob = await saveTB3Project({
				code: editor.value,
				settings: {
					mode: this.mode,
					sampleRate: this.sampleRate,
					drawMode: scope.drawMode,
					scale: scope.drawScale
				},
				audioFiles: this.audioFiles
			});
			const url = URL.createObjectURL(blob);
			ui.downloader.href = url;
			ui.downloader.download = 'project.tb3';
			ui.downloader.click();
			setTimeout(() => URL.revokeObjectURL(url));
			if(status) {
				status.textContent = 'TB3 project saved.';
			}
		} catch(error) {
			if(status) {
				status.textContent = 'Unable to save project: ' + error.message;
			}
		}
	}
	oninputCounter(event) {
		if(event.key === 'Enter') {
			ui.controlTime.blur();
			this.playbackToggle(true);
			return;
		}
		const byteSample = this.settings.isSeconds ? Math.round(ui.controlTime.value * this.sampleRate) :
			ui.controlTime.value;
		this.setByteSample(byteSample);
		this.sendData({ byteSample });
	}
	parseUrl() {
		let urlHash = window.location.hash;
		if(!urlHash) {
			this.updateUrl();
			urlHash = window.location.hash;
		}
		this.loadCode(getCodeFromUrl(urlHash) || { code: editor.value }, false);
	}
	playbackStop() {
		this.playbackToggle(false, false);
		this.sendData({ isPlaying: false, resetTime: true });
	}
	playbackToggle(isPlaying, isSendData = true, speedIncrement = 0) {
		const isReverse = speedIncrement ? speedIncrement < 0 : this.playbackSpeed < 0;
		const buttonElem = isReverse ? ui.controlPlayBackward : ui.controlPlayForward;
		if(speedIncrement && buttonElem.getAttribute('disabled')) {
			return;
		}
		const multiplierElem = buttonElem.firstElementChild;
		const speed = speedIncrement ? +multiplierElem.textContent : 1;
		multiplierElem.classList.toggle('control-fast-multiplier-large', speed >= 8);
		const nextSpeed = speed === 64 ? 0 : speed * 2;
		ui.setPlayButton(ui.controlPlayBackward, isPlaying && isReverse ? nextSpeed : 1);
		ui.setPlayButton(ui.controlPlayForward, isPlaying && !isReverse ? nextSpeed : 1);
		if(speedIncrement || !isPlaying) {
			this.playbackSpeed = isPlaying ? speedIncrement * speed : Math.sign(this.playbackSpeed);
		}
		scope.canvasContainer.title = isPlaying ? `Click to ${
			this.isRecording ? 'pause and stop recording' : 'pause' }` :
			`Click to play${ isReverse ? ' in reverse' : '' }`;
		scope.canvasPlayButton.classList.toggle('canvas-play-backward', isReverse);
		scope.canvasPlayButton.classList.toggle('canvas-play', !isPlaying);
		scope.canvasPlayButton.classList.toggle('canvas-pause', isPlaying);
		if(isPlaying) {
			scope.canvasPlayButton.classList.remove('canvas-initial');
			if(this.audioCtx.resume) {
				this.audioCtx.resume();
				scope.requestAnimationFrame(); // Main call for drawing in the scope
			}
		} else {
			this.lastUpdateTime = 0;
			this.updateCounter = 0;
			this.isLagging = false;
			ui.controlLag.innerText = '---';
			ui.controlLag.classList.remove('control-lag-red');
			if(this.isRecording) {
				this.isRecording = false;
				ui.controlRecord.classList.remove('control-recording');
				ui.controlRecord.title = 'Record to file';
				this.audioRecorder.stop();
			}
		}
		this.isPlaying = isPlaying;
		if(isSendData) {
			this.sendData({ isPlaying, playbackSpeed: this.playbackSpeed });
		} else {
			this.isNeedClear = true;
		}
	}
	receiveData(data) {
		const { byteSample, drawBuffer, error } = data;
		if(typeof byteSample === 'number') {
			this.setCounterValue(byteSample);
			this.setByteSample(byteSample);
		}
		if(Array.isArray(drawBuffer)) {
			scope.drawBuffer = scope.drawBuffer.concat(drawBuffer);
			const limit = scope.canvasWidth * (1 << scope.drawScale) - 1;
			if(scope.drawBuffer.length > limit) {
				scope.drawBuffer = scope.drawBuffer.slice(-limit);
			}
		}
		if(error !== undefined) {
			let isUpdate = false;
			if(error.isCompiled === false) {
				isUpdate = true;
				this.isCompilationError = true;
			} else if(error.isCompiled === true) {
				isUpdate = true;
				this.isCompilationError = false;
			} else if(error.isRuntime === true && !this.isCompilationError) {
				isUpdate = true;
			}
			if(isUpdate) {
				editor.errorElem.innerText = error.message;
				this.sendData({ errorDisplayed: true });
			}
			if(data.updateUrl !== true) {
				ui.setCodeSize(editor.value);
			}
		}
		if(data.updateUrl === true) {
			this.updateUrl();
		}
	}
	resetScopeAdjustment() {
		if(scope.isAnalyzerMode) {
			this.setFFTBins(-scope.fftSize + 10);
		} else {
			this.setScale(-scope.drawScale);
		}
	}
	resetTime() {
		this.isNeedClear = true;
		this.sendData({ resetTime: true, playbackSpeed: this.playbackSpeed });
	}
	saveSettings() {
		this.settings.drawMode = scope.drawMode;
		this.settings.drawScale = scope.drawScale;
		this.settings.fftSize = scope.fftSize;
		this.settings.showAllSongs = library.showAllSongs;
		localStorage.settings = JSON.stringify(this.settings);
	}
	sendData(data) {
		this.audioWorkletNode.port.postMessage(data);
	}
	setByteSample(value) {
		this.byteSample = +value || 0;
		if(this.isNeedClear && value === 0) {
			this.isNeedClear = false;
			scope.drawBuffer = [];
			scope.canvasTimeCursor.style.left = 0;
			scope.clearCanvas();
			if(!this.isPlaying) {
				scope.canvasPlayButton.classList.add('canvas-initial');
			}
		}
	}
	setCodeStyle(value) {
		if(value !== undefined) {
			this.settings.codeStyle = value;
			this.saveSettings();
		} else if((value = this.settings.codeStyle) === undefined) {
			value = this.settings.codeStyle = this.defaultSettings.codeStyle;
			this.saveSettings();
		}
		document.documentElement.dataset.syntax = value;
		document.documentElement.dataset.syntaxType = value.endsWith('Light') ? 'light' : 'dark';
	}
	setColorDiagram(value) {
		if(value !== undefined) {
			this.settings.colorDiagram = value;
			this.saveSettings();
		} else if((value = this.settings.colorDiagram) === undefined) {
			value = this.settings.colorDiagram = this.defaultSettings.colorDiagram;
			this.saveSettings();
		}
		ui.controlColorDiagram.value = value;
		ui.controlColorDiagramInfo.innerHTML = scope.getColorTest('colorDiagram', value);
	}
	setColorStereo(value) {
		// Color combinations: the first array is the left channel, the second is the right.
		if(value !== undefined) {
			this.settings.colorStereo = value;
			this.saveSettings();
		} else if((value = this.settings.colorStereo) === undefined) {
			value = this.settings.colorStereo = this.defaultSettings.colorStereo;
			this.saveSettings();
		}
		ui.controlColorStereo.value = value;
		switch(value) {
		case 0: scope.colorChannels = [[0], [1, 2]]; break;
		case 2: scope.colorChannels = [[2], [0, 1]]; break;
		case 3: scope.colorChannels = [[0, 1], [2]]; break;
		case 4: scope.colorChannels = [[0, 2], [1]]; break;
		case 5: scope.colorChannels = [[1, 2], [0]]; break;
		default: scope.colorChannels = [[1], [0, 2]];
		}
		if(scope.colorWaveform) {
			scope.setStereoColors();
		}
	}
	setColorTimeCursor(value) {
		if(value !== undefined) {
			this.settings.colorTimeCursor = value;
			this.saveSettings();
		} else if((value = this.settings.colorTimeCursor) === undefined) {
			value = this.settings.colorTimeCursor = this.defaultSettings.colorTimeCursor;
			this.saveSettings();
		}
		ui.controlColorTimeCursor.value = value;
		scope.canvasTimeCursor.style.borderLeft = '2px solid ' + value;
	}
	setColorWaveform(value) {
		if(value !== undefined) {
			this.settings.colorWaveform = value;
			this.saveSettings();
		} else if((value = this.settings.colorWaveform) === undefined) {
			value = this.settings.colorWaveform = this.defaultSettings.colorWaveform;
			this.saveSettings();
		}
		ui.controlColorWaveform.value = value;
		ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform', value);
		scope.setStereoColors();
	}
	setCounterUnits() {
		ui.controlTimeUnits.textContent = this.settings.isSeconds ? 'sec' : 't';
		this.setCounterValue(this.byteSample);
	}
	setCounterValue(value) {
		ui.controlTime.value = this.settings.isSeconds ? (value / this.sampleRate).toFixed(2) : value;
		// Lag detection
		this.updateCounter++;
		if(this.updateCounter === 400) {
			this.updateCounter = 0;
			const time = Date.now();
			if(this.lastUpdateTime) {
				const lag =
					Math.min(Math.max(Math.round((time - this.lastUpdateTime) * 37.5 / 400) - 100, 0), 999);
				ui.controlLag.innerText = lag + '%';
				if(lag > 3) {
					if(!this.isLagging) {
						this.isLagging = true;
						ui.controlLag.classList.add('control-lag-red');
					}
				} else if(this.isLagging) {
					this.isLagging = false;
					ui.controlLag.classList.remove('control-lag-red');
				}
			}
			this.lastUpdateTime = time;
		}
	}
	setDrawMode(drawMode) {
		scope.drawMode = drawMode;
		this.setScopeAdjustment(0);
		scope.toggleTimeCursor();
		scope.clearCanvas();
		this.saveSettings();
		this.sendData({ drawMode });
	}
	setFFTBins(amount, buttonElem) {
		if(buttonElem?.getAttribute('disabled')) {
			return;
		}
		scope.setFFTSize(scope.fftSize + amount);
		scope.setFFTAnalyzer();
		scope.clearCanvas();
		this.saveSettings();
		ui.setControlScale(scope.fftSize >= 15, scope.fftSize <= 5,
			scope.fftSize < 10 ? 2 ** scope.fftSize : `<sub>2</sub>${ scope.fftSize }`);
	}
	setPlaybackMode(mode) {
		this.mode = mode;
		ui.setModeParameters(mode, this.settings.customRangeBits, this.settings.quantizedLevels);
		this.updateUrl();
		this.sendData({ mode, customRangeBits: this.settings.customRangeBits,
			quantizedLevels: this.settings.quantizedLevels, setFunction: editor.value });
	}
	setCustomRangeBits(value) {
		this.settings.customRangeBits = Math.min(16, Math.max(1, Math.round(+value || 10)));
		ui.setModeParameters(this.mode, this.settings.customRangeBits, this.settings.quantizedLevels);
		this.saveSettings();
		this.sendData({ customRangeBits: this.settings.customRangeBits });
	}
	setQuantizedLevels(value) {
		this.settings.quantizedLevels = Math.min(32, Math.max(2, Math.round(+value || 16)));
		ui.setModeParameters(this.mode, this.settings.customRangeBits, this.settings.quantizedLevels);
		this.saveSettings();
		this.sendData({ quantizedLevels: this.settings.quantizedLevels });
	}
	setSampleRate(sampleRate, isSendData = true) {
		if(!sampleRate || !isFinite(sampleRate) ||
			// Float32 limit
			(sampleRate = Number(parseFloat(Math.abs(sampleRate)).toFixed(3))) > 3.4028234663852886E+38
		) {
			sampleRate = 8000;
		}
		sampleRate = Math.max(0.1, sampleRate);
		switch(sampleRate) {
		case 8000:
		case 9000:
		case 9600:
		case 10000:
		case 11025:
		case 12000:
		case 16000:
		case 18000:
		case 22050:
		case 24000:
		case 32000:
		case 33075:
		case 36000:
		case 38400:
		case 42000:
		case 44100:
		case 48000: ui.controlSampleRateSelect.value = sampleRate; break;
		default: ui.controlSampleRateSelect.selectedIndex = -1;
		}
		const oldSampleRate = this.sampleRate;
		ui.controlSampleRate.value = this.sampleRate = sampleRate;
		ui.controlSampleRate.blur();
		ui.controlSampleRateSelect.blur();
		scope.toggleTimeCursor();
		if(isSendData) {
			const data = {
				sampleRate: this.sampleRate,
				sampleRatio: this.sampleRate / this.audioCtx.sampleRate
			};
			if(this.mode === 'Funcbeat' || this.mode === 'Signed Funcbeat') {
				data.byteSample = Math.round(ui.controlTime.value * sampleRate /
					(this.settings.isSeconds ? 1 : oldSampleRate));
				this.setCounterValue(data.byteSample);
				this.setByteSample(data.byteSample);
			}
			this.updateUrl();
			this.sendData(data);
		}
	}
	setScale(amount, buttonElem) {
		if(buttonElem?.getAttribute('disabled')) {
			return;
		}
		scope.drawScale = Math.min(Math.max(scope.drawScale + amount, 0), 20);
		scope.toggleTimeCursor();
		scope.clearCanvas();
		this.saveSettings();
		ui.setControlScale(scope.drawScale <= 0, scope.drawScale >= 20,
			!scope.drawScale ? '1x' :
			scope.drawScale < 7 ? `1/${ 2 ** scope.drawScale }${ scope.drawScale < 4 ? 'x' : '' }` :
			`<sub>2</sub>-${ scope.drawScale }`);
	}
	setScopeAdjustment(amount, buttonElem) {
		if(scope.isAnalyzerMode) {
			ui.controlScaleDown.title = 'Use more analyzer bins';
			ui.controlScaleUp.title = 'Use less analyzer bins';
			ui.controlScale.title = 'Analyzer bins. Click to reset to 1024';
			this.setFFTBins(-amount, buttonElem);
		} else {
			ui.controlScaleDown.title = 'Zoom in the scope';
			ui.controlScaleUp.title = 'Zoom out the scope';
			ui.controlScale.title = 'Scope zoom factor. Click to reset to 1.';
			this.setScale(amount, buttonElem);
		}
	}
	setSRDivisor(increment) {
		const value = (this.settings.srDivisor || 1) + increment;
		if(value === 0) {
			return;
		}
		ui.controlSRDivisor.textContent = this.settings.srDivisor = value;
		this.saveSettings();
		this.sendData({ srDivisor: value });
	}
	setThemeStyle(value) {
		if(value === undefined) {
			if((value = this.settings.themeStyle) === undefined) {
				value = this.settings.themeStyle = this.defaultSettings.themeStyle;
				this.saveSettings();
			}
			document.documentElement.dataset.theme = value;
			document.documentElement.dataset.themeType = value.endsWith('Light') ? 'light' : 'dark';
			return;
		}
		document.documentElement.dataset.theme = this.settings.themeStyle = value;
		document.documentElement.dataset.themeType = value.endsWith('Light') ? 'light' : 'dark';
		let colorCursor = '#80c0ff';
		let colorDiagram = '#0080ff';
		let colorStereo = 1; // Red=0, Green=1, Blue=2
		let { codeStyle } = this.defaultSettings;
		switch(value) {
		case 'Blue Dark': codeStyle = 'Github Dark'; break;
		case 'Cake Dark':
			colorCursor = '#40ffff';
			colorDiagram = '#c000c0';
			colorStereo = 0;
			codeStyle = 'Monokai';
			break;
		case 'Dusk Dark': codeStyle = 'One Dark'; break;
		case 'Green Dark':
			colorCursor = '#00ffa8';
			colorDiagram = '#00a080';
			codeStyle = 'Retro Green';
			break;
		case 'Orange Dark':
			colorCursor = '#ffff80';
			colorDiagram = '#8000ff';
			colorStereo = 0;
			codeStyle = 'Github v2 Dark';
			break;
		case 'Purple Dark':
			colorCursor = '#ff50ff';
			colorDiagram = '#a040ff';
			colorStereo = 0;
			codeStyle = 'Atom Dark';
			break;
		case 'Teal Dark':
			colorCursor = '#80c0ff';
			colorDiagram = '#00a0c0';
			codeStyle = 'VSCode Dark';
			break;
		case 'Red Dark':
			colorCursor = '#ff8a8a';
			colorDiagram = '#e05252';
			colorStereo = 0;
			codeStyle = 'Github Dark';
			break;
		case 'Yellow Dark':
			colorCursor = '#fff080';
			colorDiagram = '#d5b53a';
			codeStyle = 'Github Dark';
			break;
		case 'Lime Dark':
			colorCursor = '#c9ff72';
			colorDiagram = '#8ccf3f';
			codeStyle = 'Retro Green';
			break;
		case 'Pink Dark':
			colorCursor = '#ff9dc5';
			colorDiagram = '#e36aa2';
			colorStereo = 0;
			codeStyle = 'Atom Dark';
			break;
		case 'Grey Dark':
			colorCursor = '#d3dbe3';
			colorDiagram = '#8d9aa8';
			codeStyle = 'VSCode Dark';
			break;
		case 'Forest Dark':
			colorCursor = '#8ae0ae';
			colorDiagram = '#2f9d65';
			codeStyle = 'Retro Green';
			break;
		case 'Monochrome Dark':
			colorCursor = '#e0e5ea';
			colorDiagram = '#9aa3ad';
			codeStyle = 'VSCode Dark';
			break;
		case 'Sepia Dark':
			colorCursor = '#f0c48a';
			colorDiagram = '#c28c53';
			colorStereo = 0;
			codeStyle = 'Github v2 Dark';
			break;
		case 'Chasyxx Dark':
			colorCursor = '#5bd7ae';
			colorDiagram = '#23906b';
			codeStyle = 'Retro Green';
			break;
		case 'Chasyxx Classic Dark':
			colorCursor = '#5dfd57';
			colorDiagram = '#42af3e';
			codeStyle = 'Retro Green';
			break;
		case 'T Dark':
			colorCursor = '#88eefb';
			colorDiagram = '#ca89ff';
			colorStereo = 2;
			codeStyle = 'One Dark';
			break;
		case 'BSII Dark':
			colorCursor = '#94c7ff';
			colorDiagram = '#5876ce';
			colorStereo = 2;
			codeStyle = 'Github Dark';
			break;
		case 'D3nsch0t Dark':
			colorCursor = '#00ffff';
			colorDiagram = '#0020a0';
			colorStereo = 2;
			codeStyle = 'Github Dark';
			break;
		case 'Void Dark (formerly Dyemagenta Dark)':
			colorCursor = '#9999ff';
			colorDiagram = '#5599ff';
			colorStereo = 2;
			codeStyle = 'Github Dark';
			break;
		case 'KOIII7337 Dark':
			colorCursor = '#57ffff';
			colorDiagram = '#008f68';
			codeStyle = 'Retro Green';
			break;
		case 'EnBeat (asd2ed) Dark':
			colorCursor = '#ff5a00';
			colorDiagram = '#ffff00';
			colorStereo = 0;
			codeStyle = 'Atom Dark';
			break;
		case 'GFL Dark':
			colorCursor = '#80c0ff';
			colorDiagram = '#0080ff';
			colorStereo = 1;
			codeStyle = 'Atom Dark';
			break;
		case 'LevelPack1218s Dark':
			colorCursor = '#f7b000';
			colorDiagram = '#28a94a';
			colorStereo = 1;
			codeStyle = 'Atom Dark';
			break;
		case 'LuddeBeat2/GiraffeFamilyLifeJungleSimOmegaFrogFile2100 (wow long username) Dark':
			colorCursor = '#ffd800';
			colorDiagram = '#00ffff';
			colorStereo = 1;
			codeStyle = 'Atom Dark';
			break;
		case 'NewBeat Dark':
			colorCursor = '#ef3a0b';
			colorDiagram = '#9c1a0a';
			colorStereo = 0;
			codeStyle = 'Github v2 Dark';
			break;
		case 'UltraDasher965 Dark':
			colorCursor = '#ffbbff';
			colorDiagram = '#995599';
			colorStereo = 0;
			codeStyle = 'Atom Dark';
			break;
		case 'Rainbow Dark':
			colorCursor = '#ff84d8';
			colorDiagram = '#a78bfa';
			colorStereo = 0;
			codeStyle = 'Atom Dark';
			break;
		case 'Default Light': codeStyle = 'Atom Light'; break;
		case 'Blue Light': codeStyle = 'Github Light'; break;
		case 'Cake Light':
			colorCursor = '#d94fb8';
			colorDiagram = '#b52fa8';
			colorStereo = 0;
			codeStyle = 'Atom Light';
			break;
		case 'Dusk Light':
			colorCursor = '#7060b8';
			colorDiagram = '#665c9e';
			codeStyle = 'One Light';
			break;
		case 'Green Light': codeStyle = 'VSCode Light'; break;
		case 'Orange Light':
			colorCursor = '#dc651c';
			colorDiagram = '#c64f0d';
			colorStereo = 0;
			codeStyle = 'Github v2 Light';
			break;
		case 'Purple Light':
			colorCursor = '#a34dcc';
			colorDiagram = '#8840b8';
			colorStereo = 0;
			codeStyle = 'Atom Light';
			break;
		case 'Teal Light':
			colorCursor = '#008c91';
			colorDiagram = '#007d82';
			codeStyle = 'VSCode Light';
			break;
		case 'Red Light':
			colorCursor = '#e76b73';
			colorDiagram = '#c52f3a';
			colorStereo = 0;
			codeStyle = 'Github Light';
			break;
		case 'Yellow Light':
			colorCursor = '#b58a00';
			colorDiagram = '#a87900';
			codeStyle = 'Github Light';
			break;
		case 'Lime Light':
			colorCursor = '#6f9e22';
			colorDiagram = '#5d8c00';
			codeStyle = 'VSCode Light';
			break;
		case 'Pink Light':
			colorCursor = '#d9568c';
			colorDiagram = '#c53b78';
			colorStereo = 0;
			codeStyle = 'Atom Light';
			break;
		case 'Grey Light':
			colorCursor = '#687582';
			colorDiagram = '#66717d';
			codeStyle = 'VSCode Light';
			break;
		case 'Forest Light':
			colorCursor = '#2f895b';
			colorDiagram = '#26734c';
			codeStyle = 'VSCode Light';
			break;
		case 'Monochrome Light':
			colorCursor = '#707983';
			colorDiagram = '#5f6872';
			codeStyle = 'Github Light';
			break;
		case 'Sepia Light':
			colorCursor = '#a87545';
			colorDiagram = '#9a6537';
			colorStereo = 0;
			codeStyle = 'Github v2 Light';
			break;
		case 'Rainbow Light':
			colorCursor = '#e064b4';
			colorDiagram = '#8d4bb8';
			colorStereo = 0;
			codeStyle = 'Atom Light';
			break;
		}
		this.setColorTimeCursor(colorCursor);
		this.setColorStereo(colorStereo);
		ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform');
		this.setColorDiagram(ui.controlColorDiagram.value = colorDiagram); // Contains this.saveSettings();
		this.setCodeStyle(codeStyle);
		ui.controlCodeStyle.value = codeStyle;
	}
	setVolume(isInit) {
		let volumeValue = NaN;
		if(isInit) {
			volumeValue = parseFloat(this.settings.volume);
		}
		if(isNaN(volumeValue)) {
			volumeValue = ui.controlVolume.value / ui.controlVolume.max;
		}
		ui.controlVolume.value = this.settings.volume = volumeValue;
		ui.controlVolume.title = `Volume: ${ (volumeValue * 100).toFixed(2) }%`;
		this.saveSettings();
		this.audioGain.gain.value = volumeValue * volumeValue;
	}
	toggleCounterUnits() {
		this.settings.isSeconds = !this.settings.isSeconds;
		this.saveSettings();
		this.setCounterUnits();
	}
	toggleRecording() {
		if(!this.audioCtx) {
			return;
		}
		if(this.isRecording) {
			this.playbackToggle(false);
			return;
		}
		this.isRecording = true;
		ui.controlRecord.classList.add('control-recording');
		ui.controlRecord.title = 'Pause and stop recording';
		this.audioRecorder.start();
		this.audioRecordChunks = [];
		this.playbackToggle(true);
	}
	updateUrl() {
		const code = editor.value;
		ui.setCodeSize(code);
		getUrlFromCode(code, this.mode, this.sampleRate);
	}
}();
